import { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Box,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { ref, getDownloadURL } from 'firebase/storage';
import { formatDate } from '../../utils';
import { useStorage } from '../../utils/firebase';
import { TEXT_FIELD_REGEX, TEXT_FIELD_MESSAGE } from '../../constants/fieldLimits';

const ResolutionDialog = ({
  isDialogOpen = true,
  setDialogOpen,
  requestedBy = { firstName: '', lastName: '' },
  vacationFrom,
  vacationTo,
  registryNumber = '',
  setRegistryNumber,
  onPreview,
  onSend,
  isProcessing,
  isReissue = false,
}) => {
  const storage = useStorage();
  const [step, setStep] = useState('input');
  const [pdfUrls, setPdfUrls] = useState([]);
  const [registryNumberError, setRegistryNumberError] = useState(null);

  useEffect(() => {
    if (!isDialogOpen) {
      setStep('input');
      setPdfUrls([]);
      setRegistryNumberError(null);
    }
  }, [isDialogOpen]);

  const handleRegistryNumberChange = e => {
    const val = e.target.value;
    setRegistryNumber(val);
    setRegistryNumberError(TEXT_FIELD_REGEX.test(val) ? null : TEXT_FIELD_MESSAGE);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handlePreview = async () => {
    try {
      const result = await onPreview({ registryNumber });
      const urls = await Promise.all(
        result.pdfs.map(pdf => getDownloadURL(ref(storage, pdf.storagePath))),
      );
      setPdfUrls(urls);
      setStep('preview');
    } catch {
      // error handled in parent via snackbar
    }
  };

  return (
    <Dialog
      open={isDialogOpen}
      onClose={handleClose}
      data-cy="resolution-dialog"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {step === 'input' ? 'Issue Decision on Annual Leave' : 'Decision on Annual Leave Preview'}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="large"
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {step === 'input' ? (
          <>
            <DialogContentText sx={{ marginBottom: 2 }}>
              {requestedBy.firstName} {requestedBy.lastName} &middot;{' '}
              {formatDate(vacationFrom, 'MMMM dd, yyyy')}
              {formatDate(vacationFrom, 'MMMM dd, yyyy') !==
                formatDate(vacationTo, 'MMMM dd, yyyy') &&
                ` – ${formatDate(vacationTo, 'MMMM dd, yyyy')}`}
            </DialogContentText>
            {isReissue && (
              <DialogContentText sx={{ marginBottom: 2, color: 'warning.main' }}>
                Generating a new resolution will replace the previously issued one.
              </DialogContentText>
            )}
            <TextField
              fullWidth
              id="registryNumber"
              label="Registry number"
              name="registryNumber"
              placeholder="03-01/17"
              value={registryNumber}
              onChange={handleRegistryNumberChange}
              margin="normal"
              required
              error={!!registryNumberError}
              helperText={registryNumberError || 'Format example: 03-01/17'}
              slotProps={{
                htmlInput: { maxLength: 100 },
                inputLabel: { shrink: true },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                        Del.br.:
                      </Box>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </>
        ) : (
          <>
            <DialogContentText sx={{ marginBottom: 2 }}>
              {requestedBy.firstName} {requestedBy.lastName} &middot;{' '}
              {formatDate(vacationFrom, 'MMMM dd, yyyy')}
              {formatDate(vacationFrom, 'MMMM dd, yyyy') !==
                formatDate(vacationTo, 'MMMM dd, yyyy') &&
                ` – ${formatDate(vacationTo, 'MMMM dd, yyyy')}`}
              <br />
              Del.br.: {registryNumber}
            </DialogContentText>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pdfUrls.map((url, i) => (
                <Link
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-cy={`resolutionPreviewLink${i}`}
                >
                  {pdfUrls.length > 1 ? `Resolution ${i + 1}` : 'Resolution'} — open PDF
                </Link>
              ))}
            </Box>
          </>
        )}
        <DialogActions sx={{ px: 0, pt: 2 }}>
          {step === 'input' ? (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                disabled={!registryNumber.trim() || !!registryNumberError || isProcessing}
                onClick={handlePreview}
                data-cy="previewResolutionButton"
                startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isProcessing ? 'Generating...' : 'Generate'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setStep('input')}>Back</Button>
              <Button disabled={isProcessing} onClick={onSend} data-cy="sendResolutionButton">
                Send
              </Button>
            </>
          )}
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default ResolutionDialog;
